"use client";
import { useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { useBreakpoint } from "@/shared/hooks/use-breakpoint";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode, Pagination, Autoplay } from "swiper/modules";
import { Swiper as SwiperType } from "swiper";

import "swiper/css";
import "swiper/css/pagination";

const impactList = [
  {
    id: "01.",
    title: "mn_ei_title_1",
    description: "mn_ei_context_1",
  },
  {
    id: "02.",
    title: "mn_ei_title_2",
    description: "mn_ei_context_2",
  },
  {
    id: "03.",
    title: "mn_ei_title_3",
    description: "mn_ei_context_3",
  },
  {
    id: "04.",
    title: "mn_ei_title_4",
    description: "mn_ei_context_4",
  },
  {
    id: "05.",
    title: "mn_ei_title_5",
    description: "mn_ei_context_5",
  },
  {
    id: "06.",
    title: "mn_ei_title_6",
    description: "mn_ei_context_6",
  },
];

// PC용 ImpactSection
function ImpactSectionPC() {
  const t = useTranslations();

  const [activeIndex, setActiveIndex] = useState(0);

  const handleSlideChange = (swiper: SwiperType) => {
    setActiveIndex(swiper.realIndex);
  };

  return (
    <div className="py-30">
      <div className="relative m-auto w-full max-w-[1280px]">
        <h1 className="heading-1 absolute left-0 text-gray-900">{t("mn_ei")}</h1>

        <div className="ml-[433px] min-w-screen overflow-hidden">
          <Swiper
            modules={[Pagination, Autoplay]}
            onSlideChange={handleSlideChange}
            slidesPerView={1}
            spaceBetween={20}
            pagination={{ type: "progressbar" }}
            autoplay={{ delay: 2500 }}
            loop={true}
            loopAdditionalSlides={2}
            className="!m-0 w-[414px] max-w-full !overflow-visible !pb-15 [&_.swiper-pagination]:!left-0 [&_.swiper-pagination]:!w-[848px]"
          >
            {impactList.map((impact, index) => (
              <SwiperSlide key={impact.id} className="opacity-100">
                <div
                  className={`rounded-24 h-[463px] p-10 pt-15 transition-all duration-300 ${
                    index === activeIndex ? "bg-primary-800" : "bg-primary-100"
                  }`}
                >
                  <div
                    className={`heading-2 mb-[118px] ${
                      index === activeIndex ? "text-white/20" : "text-secondary-500/20"
                    }`}
                  >
                    {impact.id}
                  </div>
                  <div
                    className={`flex flex-col gap-4 whitespace-pre-line ${index === activeIndex ? "text-white" : "text-secondary-500"}`}
                  >
                    <h4 className={`heading-4-bold`}>{t(impact.title)}</h4>
                    <p className={`body-md-regular`}>{t(impact.description)}</p>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </div>
  );
}

// 모바일용 ImpactSection
function ImpactSectionMobile() {
  const { isTablet } = useBreakpoint();
  const t = useTranslations();
  const [activeIndex, setActiveIndex] = useState(0);
  return (
    <div className="tb:py-30 tb:px-10 tb:pr-0 px-4 py-20">
      <h1 className="heading-1 tb:mb-10 mb-5 text-gray-900">{t("mn_ei")}</h1>

      <Swiper
        pagination={{ type: "progressbar" }}
        slidesPerView={isTablet ? 1.5 : 1}
        spaceBetween={isTablet ? 16 : 12}
        modules={[Pagination, FreeMode, Autoplay]}
        freeMode={isTablet ? true : false}
        autoplay={{ delay: 2500 }}
        loop={true}
        loopAdditionalSlides={1}
        onSlideChange={(swiper) => {
          setActiveIndex(swiper.realIndex);
        }}
        className="tb:!pb-15 !pb-5"
      >
        {impactList.map((impact, index) => (
          <SwiperSlide
            key={impact.id}
            className={`tb:rounded-24 tb:p-10 tb:pt-15 tb:max-h-115 tb:min-w-[414px] min-h-[244px] rounded-2xl p-6 opacity-100 ${index === activeIndex ? "bg-primary-800" : "bg-primary-100"}`}
          >
            <div className="tb:gap-45 flex flex-col gap-10">
              <div className={`heading-2 ${index === activeIndex ? "text-white/30" : "text-secondary-500/30"}`}>
                {impact.id}
              </div>

              <div className="tb:gap-4 flex flex-col gap-3">
                <h4
                  className={`heading-4-bold whitespace-pre-line ${index === activeIndex ? "text-white" : "text-secondary-500"}`}
                >
                  {t(impact.title)}
                </h4>
                <p className={`body-md-regular ${index === activeIndex ? "text-white" : "text-secondary-500"}`}>
                  {t(impact.description)}
                </p>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}

export function ImpactSection() {
  const { isPc } = useBreakpoint();

  return isPc ? <ImpactSectionPC /> : <ImpactSectionMobile />;
}
